import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Divider,
  Switch,
  Typography,
  Input,
  message,
  Spin,
  Button,
  Tag,
  Tooltip,
  Tabs,
  Slider,
  Checkbox,
  Radio,
} from 'antd';

const { Text } = Typography;
import { useGetSessionReplay, usePostSessionReplay } from '@src/api/emp/queryHooks';
import {
  CheckOutlined,
  DeleteOutlined,
  RollbackOutlined,
  GlobalOutlined,
  AndroidOutlined,
  AppleOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useInternalAdmin } from '@hooks';
import styled from 'styled-components';

import type {
  AndroidDraftConfig,
  CompressionQuality,
  IosDraftConfig,
  JsDraftConfig,
  MaskOptionsConfigShape,
  SelectorType,
  SessionReplayApiData,
  SessionReplayPlatformNode,
} from './types';
import {
  ADVANCED_BULK_EXTENSIONS,
  ANDROID_DEFAULT_MASK_INPUT_OPTIONS,
  ANDROID_MASK_INPUT_OPTIONS,
  COMPRESSION_QUALITY_OPTIONS,
  EMPTY_ANDROID_CONFIG,
  EMPTY_IOS_CONFIG,
  EMPTY_JS_CONFIG,
  IOS_DEFAULT_MASK_INPUT_OPTIONS,
  IOS_MASK_INPUT_OPTIONS,
  LOCKED_MANUAL_BADGES,
  OTHER_MASKING_TYPES,
  SEARCH_EXTENSION,
} from './constants';
import {
  arraysEqual,
  clampPercentage,
  normalizeApiData,
  normalizeAndroidConfig,
  normalizeCompressionQuality,
  normalizeIosConfig,
  normalizeJsConfig,
  normalizeMaskInputOptions,
  normalizeSelector,
} from './normalize';
import {
  cardStyle,
  DEFAULT_BADGE_STYLE,
  OPTION_BUTTON_BASE_STYLE,
  OPTION_GRID_STYLE,
  SAMPLING_INPUT_STYLE,
  SAMPLING_READONLY_STYLE,
} from './styled';

export const BulletCustom = styled.span`
  font-size: 14px;
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 5px;
  height: 5px;
  background-color: rgba(119, 119, 119, 1);
  border-radius: 50%;
`;

const TagInput: React.FC<{
  label: string;
  description: string;
  tags: string[];
  isDeleted: (tag: string) => boolean;
  isFromOriginal: (tag: string) => boolean;
  onAdd: (selector: string) => void;
  onRemoveNew: (selector: string) => void;
  onDeleteExisting: (selector: string) => void;
  onRestoreExisting: (selector: string) => void;
  placeholder?: string;
  disabled?: boolean;
}> = ({
  label,
  description,
  tags,
  isDeleted,
  isFromOriginal,
  onAdd,
  onRemoveNew,
  onDeleteExisting,
  onRestoreExisting,
  placeholder,
  disabled,
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    setInputValue('');
  };

  return (
    <div style={{ background: 'rgba(248, 250, 252, 1)', padding: 16, borderRadius: 16, border: '1px solid rgba(226, 232, 240, 1)' }}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 14 }}>{label}</div>
        <div style={{ fontSize: 12, color: 'rgba(100, 116, 139, 1)', lineHeight: 1.3 }}>{description}</div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        {tags.length === 0 ? (
          <span style={{ fontSize: 12, color: 'rgba(148, 163, 184, 1)', fontStyle: 'italic', padding: '4px 0' }}>
            No selectors added yet.
          </span>
        ) : null}

        {tags.map((tag) => {
          const deleted = isDeleted(tag);
          const fromOriginal = isFromOriginal(tag);
          const isNew = !fromOriginal;
          return (
            <span key={`existing-${label}-${tag}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Tag
                style={{
                  borderRadius: 10,
                  padding: '3px 8px',
                  marginInlineEnd: 0,
                  opacity: deleted ? 0.6 : 1,
                  textDecoration: deleted ? 'line-through' : 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
                color={deleted ? 'orange' : isNew ? 'geekblue' : 'blue'}
                data-test-id={`cssSelector-${tag}`}
              >
                <span>{tag}</span>
                {deleted ? (
                  <Tooltip title="Restore">
                    <Button
                      type="text"
                      icon={<RollbackOutlined style={{ color: 'rgba(128, 163, 255, 1)' }} />}
                      onClick={() => onRestoreExisting(tag)}
                      data-test-id={`${tag}-restoreSelectorButton`}
                      size="small"
                      style={{ marginLeft: 4 }}
                    />
                  </Tooltip>
                ) : isNew ? (
                  <Tooltip title="Remove">
                    <Button
                      type="text"
                      icon={<CloseOutlined style={{ color: 'rgba(99, 102, 241, 1)' }} />}
                      onClick={() => onRemoveNew(tag)}
                      disabled={disabled}
                      size="small"
                      style={{ marginLeft: 4 }}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title="Delete (save to apply)">
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => onDeleteExisting(tag)}
                      data-test-id={`${tag}-deleteSelectorButton`}
                      disabled={disabled}
                      size="small"
                      style={{ marginLeft: 4 }}
                    />
                  </Tooltip>
                )}
              </Tag>
            </span>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <Input
          value={inputValue}
          placeholder={placeholder || 'e.g. .my-class'}
          onChange={(e) => setInputValue(e.target.value)}
          onPressEnter={(e) => {
            e.preventDefault();
            handleAdd();
          }}
          disabled={disabled}
        />
        <Button type="primary" onClick={handleAdd} disabled={disabled}>
          Add
        </Button>
      </div>
    </div>
  );
};

const ToggleRow: React.FC<{
  title: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled: boolean;
  testId?: string;
  style?: React.CSSProperties;
}> = React.memo(({ title, description, checked, onChange, disabled, testId }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      //justifyContent: 'space-between',
      gap: 16,
      //background: 'rgba(248, 250, 252, 1)',
      border: '1px solid rgba(226, 232, 240, 1)',
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
      borderRadius: 16,
      padding: 24,
    }}
  >
    
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'normal' }}>{title}
        <Switch size="small" data-test-id={testId} onChange={onChange} checked={checked} disabled={disabled} style={{ marginLeft: '12px' }}/>
        </div>
      <div style={{ fontSize: 12, color: 'rgba(100, 116, 139, 1)' }}>{description}</div>
    </div>
    
  </div>
));
ToggleRow.displayName = 'ToggleRow';

const EnableCohortReplayCard: React.FC<{
  platform: 'Web (JS)' | 'Android' | 'iOS';
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled: boolean;
  testId: string;
}> = React.memo(({ platform, checked, onChange, disabled, testId }) => (
  <section style={{ ...cardStyle }}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <div>
        <div style={{ fontSize: 16 }}>Enable Cohort Replay
        <Switch size="small" data-test-id={testId} onChange={onChange} checked={checked} disabled={disabled}  style={{ marginLeft: 12 }}/>
        </div>
        <div style={{ fontSize: 14, color: 'rgba(100, 116, 139, 1)' }}>
          
        Switch on the toggle button to configure {platform} replays. Contact your Conviva representative to enable this feature.
        </div>
      </div>
      
    </div>
  </section>
));
EnableCohortReplayCard.displayName = 'EnableCohortReplayCard';

const TrafficSamplingSection: React.FC<{
  value: number;
  onChange: (value: number) => void;
  canEdit: boolean;
  inputTestId: string;
  sliderTestId: string;
  readOnlyTestId: string;
}> = React.memo(({ value, onChange, canEdit, inputTestId, sliderTestId, readOnlyTestId }) => (
  <section style={cardStyle}>
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'normal',
        gap: 12,
        marginBottom: 8,
      }}
    >
      <div style={{ fontSize: 18 }}>Traffic Sampling</div>
      {canEdit ? (
        <div style={{ display: 'flex', flexDirection: 'row', gap: 12 }}>
          <Slider
      min={0}
      max={100}
      step={1}
      value={value}
      tooltip={{ formatter: (v) => `${v}%` }}
      onChange={(v) => onChange(clampPercentage(typeof v === 'number' ? v : 0))}
      disabled={!canEdit}
      data-test-id={sliderTestId}
     style={{ width: '600px' }}
    />
    <Input
          type="number"
          min={0}
          max={100}
          value={value}
          onChange={(e) => {
            const parsed = Number(e.target.value);
            const nextVal = clampPercentage(Number.isFinite(parsed) ? parsed : 0);
            onChange(nextVal);
          }}
          style={SAMPLING_INPUT_STYLE}
          suffix="%"
          data-test-id={inputTestId}
        /> 
        </div>
      ) : (
        <span style={SAMPLING_READONLY_STYLE} data-test-id={readOnlyTestId}>
          {value}%
        </span>
      )}
    </div>
    <p style={{ margin: 0, color: 'rgba(100, 116, 139, 1)', fontSize: 14 }}>
    Define the percentage of sessions to be captured for Cohort Replays. Contact your Conviva representative to change the sampling
percentage.
    </p>
  </section>
));
TrafficSamplingSection.displayName = 'TrafficSamplingSection';

const DefaultMaskingBadges: React.FC<{ options: string[] }> = React.memo(({ options }) => (
  <div
    style={{
      background: 'rgba(248, 250, 252, 1)',
      border: '1px solid rgba(226, 232, 240, 1)',
      borderRadius: 16,
      padding: 16,
      marginBottom: 14,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <span
        style={{
          fontSize: 10,
          fontWeight: 800,
          color: 'rgba(71, 85, 105, 1)',
          background: 'rgba(226, 232, 240, 1)',
          padding: '2px 8px',
          borderRadius: 8,
        }}
      >
        DEFAULT
      </span>
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map((item) => (
        <span key={item} style={DEFAULT_BADGE_STYLE}>
          <CheckOutlined style={{ color: 'rgba(148, 163, 184, 1)' }} />
          {item}
        </span>
      ))}
    </div>
  </div>
));
DefaultMaskingBadges.displayName = 'DefaultMaskingBadges';

const OtherMaskingTypesGrid: React.FC<{
  allowed: string[];
  allSelected: boolean;
  someSelected: boolean;
  canEdit: boolean;
  onToggleAll: () => void;
  onToggleType: (type: string) => void;
  testIdAll: string;
  testIdForOption: (opt: string) => string;
  isChecked: (opt: string) => boolean;
  gridStyle?: React.CSSProperties;
}> = React.memo(
  ({
    allowed,
    allSelected,
    someSelected,
    canEdit,
    onToggleAll,
    onToggleType,
    testIdAll,
    testIdForOption,
    isChecked,
    gridStyle,
  }) => (
    <div style={{ ...OPTION_GRID_STYLE, ...(gridStyle || {}) }}>
      <button
        data-test-id={testIdAll}
        type="button"
        onClick={onToggleAll}
        disabled={!canEdit}
        style={{
          ...OPTION_BUTTON_BASE_STYLE,
          border: allSelected || someSelected ? '1px solid rgba(79, 70, 229, 1)' : '1px solid rgba(226, 232, 240, 1)',
          cursor: canEdit ? 'pointer' : 'not-allowed',
          opacity: canEdit ? 1 : 0.5,
          
        }}
      >
        <Checkbox checked={allSelected} indeterminate={someSelected} disabled={!canEdit} />
        <span style={{ fontSize: 14, color: 'rgba(30, 41, 59, 1)' }}>All</span>
      </button>

      {allowed.map((opt) => {
        const checked = isChecked(opt);
        return (
          <button
            key={opt}
            data-test-id={testIdForOption(opt)}
            type="button"
            onClick={() => onToggleType(opt)}
            disabled={!canEdit}
            style={{
              ...OPTION_BUTTON_BASE_STYLE,
              border: checked ? '1px solid rgba(79, 70, 229, 1)' : '1px solid rgba(226, 232, 240, 1)',
              cursor: canEdit ? 'pointer' : 'not-allowed',
              opacity: canEdit ? 1 : 0.5,
            }}
          >
            <Checkbox checked={checked} disabled={!canEdit} />
            <span style={{ fontSize: 14 }}>{opt}</span>
          </button>
        );
      })}
    </div>
  ),
);
OtherMaskingTypesGrid.displayName = 'OtherMaskingTypesGrid';

const MobileMaskingSection: React.FC<{
  defaultOptions: string[];
  allowedOptions: string[];
  config: MaskOptionsConfigShape | undefined;
  canEdit: boolean;
  onToggleAll: () => void;
  onToggleType: (type: string) => void;
  testIdAll: string;
  testIdForOption: (opt: string) => string;
  isChecked: (opt: string) => boolean;
  gridStyle?: React.CSSProperties;
}> = React.memo(
  ({
    defaultOptions,
    allowedOptions,
    config,
    canEdit,
    onToggleAll,
    onToggleType,
    testIdAll,
    testIdForOption,
    isChecked,
    gridStyle,
  }) => {
    const selectedCount = config?.maskAllInputs ? allowedOptions.length : config?.maskInputOptions?.length ?? 0;
    const allSelected = Boolean(config?.maskAllInputs) || selectedCount === allowedOptions.length;
    const someSelected = selectedCount > 0 && !allSelected;

    return (
      <section style={cardStyle}>
        <div style={{ fontSize: 18 }}>Masking of User Information</div>
        <div style={{ fontSize: 14, color: 'rgba(100, 116, 139, 1)', marginBottom: 12 }}>
        Mask PII and sensitive information in Cohort Replays across the applications.
        </div>

        <DefaultMaskingBadges options={defaultOptions} />

        <div style={{ fontSize: 14, marginBottom: 10 }}>Additional Masking Types</div>
        <OtherMaskingTypesGrid
          allowed={allowedOptions}
          allSelected={allSelected}
          someSelected={someSelected}
          canEdit={canEdit}
          onToggleAll={onToggleAll}
          onToggleType={onToggleType}
          testIdAll={testIdAll}
          testIdForOption={testIdForOption}
          isChecked={isChecked}
          gridStyle={gridStyle}
        />
      </section>
    );
  },
);
MobileMaskingSection.displayName = 'MobileMaskingSection';

const MobileAdvancedFeaturesSection: React.FC<{
  maskAllImages: boolean;
  onMaskAllImagesChange: (checked: boolean) => void;
  maskAllImagesTestId: string;
  compressionQuality: CompressionQuality;
  onCompressionQualityChange: (q: CompressionQuality) => void;
  compressionRadioTestId: string;
  compressionOptionTestId: (q: CompressionQuality) => string;
  canEdit: boolean;
  extraToggles?: Array<{
    title: string;
    description: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    testId: string;
  }>;
}> = React.memo(
  ({
    maskAllImages,
    onMaskAllImagesChange,
    maskAllImagesTestId,
    compressionQuality,
    onCompressionQualityChange,
    compressionRadioTestId,
    compressionOptionTestId,
    canEdit,
    extraToggles,
  }) => (
    <div>
      
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'normal', gap: 10, marginTop: 10, width: '100%' }}>
          <ToggleRow
            title="Mask All Images"
            description="Obscure images in the replay."
            checked={maskAllImages}
            onChange={onMaskAllImagesChange}
            disabled={!canEdit}
            testId={maskAllImagesTestId}
          />
          {extraToggles?.map((t) => (
            <ToggleRow
              key={t.testId}
              title={t.title}
              description={t.description}
              checked={t.checked}
              onChange={t.onChange}
              disabled={!canEdit}
              testId={t.testId}
              style={{ width: '100%' }}
            />
          ))}
          <div
            style={{
              //background: 'rgba(248, 250, 252, 1)',
              border: '1px solid rgba(226, 232, 240, 1)',
              borderRadius: 16,
              padding: 24,
              display: 'flex',
              //alignItems: 'center',
              flexDirection: 'column',
            }}  
          >
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'normal', gap: 10 }}>
            <span style={{ marginRight: 12}}>Quality Level</span> 
            <Radio.Group
              value={compressionQuality}
              onChange={(e) => onCompressionQualityChange(e.target.value)}
              disabled={!canEdit}
              data-test-id={compressionRadioTestId}
            >
              {COMPRESSION_QUALITY_OPTIONS.map((opt) => (
                <Radio
                  key={opt.value}
                  value={opt.value}
                  data-test-id={compressionOptionTestId(opt.value)}
                >
                  {opt.label}
                </Radio>
              ))}
            </Radio.Group>
            </div>
            <div style={{ fontSize: 12, color: 'rgba(100, 116, 139, 1)' }}>Select the quality level of the Cohort Replay.</div>
            </div>
        </div>
    </div>
  ),
);
MobileAdvancedFeaturesSection.displayName = 'MobileAdvancedFeaturesSection';

const SessionReplay = () => {
  const [deletedSelectors, setDeletedSelectors] = useState<Set<string>>(new Set());
  const { mutate: createSessionReplay } = usePostSessionReplay();
  const internalAdmin = useInternalAdmin();
  const canEdit = Boolean(internalAdmin);

  const {
    data: initialSessionReplayData,
    isLoading: isLoadingSessionReplayData,
    refetch: refetchSessionReplayData,
    isFetching: isFetchingSessionReplayData,
  } = useGetSessionReplay();

  useEffect(() => {
    refetchSessionReplayData();
  }, [refetchSessionReplayData]);

  const [apiData, setApiData] = useState<SessionReplayApiData | undefined>(
    normalizeApiData(initialSessionReplayData as any),
  );
  const [originalApiData, setOriginalApiData] = useState<SessionReplayApiData | undefined>(
    normalizeApiData(initialSessionReplayData as any),
  );

  const [jsConfig, setJsConfig] = useState<JsDraftConfig>(() =>
    normalizeJsConfig((normalizeApiData(initialSessionReplayData as any) as any)?.js?.config),
  );
  const [originalJsConfig, setOriginalJsConfig] = useState<JsDraftConfig>(() =>
    normalizeJsConfig((normalizeApiData(initialSessionReplayData as any) as any)?.js?.config),
  );

  const [androidConfig, setAndroidConfig] = useState<AndroidDraftConfig>(() =>
    normalizeAndroidConfig((normalizeApiData(initialSessionReplayData as any) as any)?.android?.config),
  );
  const [originalAndroidConfig, setOriginalAndroidConfig] = useState<AndroidDraftConfig>(() =>
    normalizeAndroidConfig((normalizeApiData(initialSessionReplayData as any) as any)?.android?.config),
  );

  const [iosConfig, setIosConfig] = useState<IosDraftConfig>(() =>
    normalizeIosConfig((normalizeApiData(initialSessionReplayData as any) as any)?.ios?.config),
  );
  const [originalIosConfig, setOriginalIosConfig] = useState<IosDraftConfig>(() =>
    normalizeIosConfig((normalizeApiData(initialSessionReplayData as any) as any)?.ios?.config),
  );

  const [isSaving, setIsSaving] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);

  const selectedOtherMaskingTypes = useMemo(() => {
    return jsConfig?.maskAllInputs ? OTHER_MASKING_TYPES : normalizeMaskInputOptions(jsConfig?.maskInputOptions);
  }, [jsConfig?.maskAllInputs, jsConfig?.maskInputOptions]);

  const manualAllSelected = selectedOtherMaskingTypes.length === OTHER_MASKING_TYPES.length;
  const manualSearchSelected = selectedOtherMaskingTypes.includes(SEARCH_EXTENSION);

  const allOtherMaskingTypesSelected = manualAllSelected;
  const someOtherMaskingTypesSelected =
    selectedOtherMaskingTypes.length > 0 && !allOtherMaskingTypesSelected;

  const toggleOtherMaskingType = useCallback(
    (type: string) => {
      if (!internalAdmin) return;
      setJsConfig((prev) => {
        const current = prev?.maskAllInputs ? OTHER_MASKING_TYPES : normalizeMaskInputOptions(prev?.maskInputOptions);
        const set = new Set(current);
        if (set.has(type)) set.delete(type);
        else set.add(type);
        const next = OTHER_MASKING_TYPES.filter((t) => set.has(t));
        const isAll = next.length === OTHER_MASKING_TYPES.length;
        return { ...(prev || EMPTY_JS_CONFIG), maskInputOptions: isAll ? OTHER_MASKING_TYPES : next, maskAllInputs: isAll };
      });
    },
    [internalAdmin],
  );

  const toggleOtherMaskingAll = useCallback(() => {
    if (!internalAdmin) return;
    setJsConfig((prev) => {
      const current = prev?.maskAllInputs ? OTHER_MASKING_TYPES : normalizeMaskInputOptions(prev?.maskInputOptions);
      const allSelected = current.length === OTHER_MASKING_TYPES.length;
      const next = allSelected ? [] : OTHER_MASKING_TYPES;
      return { ...(prev || EMPTY_JS_CONFIG), maskInputOptions: next, maskAllInputs: !allSelected };
    });
  }, [internalAdmin]);

  const toggleAndroidMaskingType = useCallback(
    (type: string) => {
      if (!internalAdmin) return;
      setAndroidConfig((prev) => {
        const current = prev?.maskAllInputs ? ANDROID_MASK_INPUT_OPTIONS : prev?.maskInputOptions || [];
        const set = new Set(current);
        if (set.has(type)) set.delete(type);
        else set.add(type);
        const next = ANDROID_MASK_INPUT_OPTIONS.filter((t) => set.has(t));
        const isAll = next.length === ANDROID_MASK_INPUT_OPTIONS.length;
        return { ...(prev || EMPTY_ANDROID_CONFIG), maskInputOptions: isAll ? ANDROID_MASK_INPUT_OPTIONS : next, maskAllInputs: isAll };
      });
    },
    [internalAdmin],
  );

  const toggleAndroidMaskingAll = useCallback(() => {
    if (!internalAdmin) return;
    setAndroidConfig((prev) => {
      const current = prev?.maskAllInputs ? ANDROID_MASK_INPUT_OPTIONS : prev?.maskInputOptions || [];
      const allSelected = current.length === ANDROID_MASK_INPUT_OPTIONS.length;
      const next = allSelected ? [] : ANDROID_MASK_INPUT_OPTIONS;
      return { ...(prev || EMPTY_ANDROID_CONFIG), maskInputOptions: next, maskAllInputs: !allSelected };
    });
  }, [internalAdmin]);

  const toggleIosMaskingType = useCallback(
    (type: string) => {
      if (!internalAdmin) return;
      setIosConfig((prev) => {
        const current = prev?.maskAllInputs ? IOS_MASK_INPUT_OPTIONS : prev?.maskInputOptions || [];
        const set = new Set(current);
        if (set.has(type)) set.delete(type);
        else set.add(type);
        const next = IOS_MASK_INPUT_OPTIONS.filter((t) => set.has(t));
        const isAll = next.length === IOS_MASK_INPUT_OPTIONS.length;
        return { ...(prev || EMPTY_IOS_CONFIG), maskInputOptions: isAll ? IOS_MASK_INPUT_OPTIONS : next, maskAllInputs: isAll };
      });
    },
    [internalAdmin],
  );

  const toggleIosMaskingAll = useCallback(() => {
    if (!internalAdmin) return;
    setIosConfig((prev) => {
      const current = prev?.maskAllInputs ? IOS_MASK_INPUT_OPTIONS : prev?.maskInputOptions || [];
      const allSelected = current.length === IOS_MASK_INPUT_OPTIONS.length;
      const next = allSelected ? [] : IOS_MASK_INPUT_OPTIONS;
      return { ...(prev || EMPTY_IOS_CONFIG), maskInputOptions: next, maskAllInputs: !allSelected };
    });
  }, [internalAdmin]);

  const originalSelectorSet = useMemo(() => {
    const all = [
      ...(originalJsConfig?.blockSelector || []),
      ...(originalJsConfig?.ignoreSelector || []),
      ...(originalJsConfig?.maskTextSelector || []),
    ].map(normalizeSelector);
    return new Set(all);
  }, [originalJsConfig]);

  const deletedSelectorSet = useMemo(() => {
    return new Set([...deletedSelectors].map(normalizeSelector));
  }, [deletedSelectors]);

  const isSelectorDeleted = useCallback(
    (selector: string) => deletedSelectorSet.has(normalizeSelector(selector)),
    [deletedSelectorSet],
  );

  const isSelectorFromOriginal = useCallback(
    (selector: string) => originalSelectorSet.has(normalizeSelector(selector)),
    [originalSelectorSet],
  );

  const addSelectorToType = useCallback(
    (type: SelectorType, selector: string) => {
      if (!internalAdmin) return;
      const trimmed = selector.trim();
      if (!trimmed) return;

      const key = normalizeSelector(trimmed);

      // If it exists but is marked deleted, restore it instead.
      if (deletedSelectorSet.has(key)) {
        setDeletedSelectors((prev) => {
          const next = new Set(prev);
          [...next].forEach((s) => {
            if (normalizeSelector(s) === key) next.delete(s);
          });
          return next;
        });
        return;
      }

      const currentAll = [
        ...(jsConfig?.blockSelector || []),
        ...(jsConfig?.ignoreSelector || []),
        ...(jsConfig?.maskTextSelector || []),
      ].map(normalizeSelector);
      if (currentAll.includes(key)) {
        message.error('This selector already exists.');
        return;
      }

      setJsConfig((prev: any) => {
        const next: any = { ...(prev || EMPTY_JS_CONFIG) };
        const listKey =
          type === 'block' ? 'blockSelector' : type === 'ignore' ? 'ignoreSelector' : 'maskTextSelector';
        const current: string[] = [...(next[listKey] || [])];
        current.push(trimmed);

        // Dedup within the list case-insensitively
        const seen = new Set<string>();
        next[listKey] = current.filter((s) => {
          const k = normalizeSelector(s);
          if (!k || seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        return next;
      });
    },
    [internalAdmin, jsConfig, deletedSelectorSet],
  );

  const removeNewSelectorFromType = useCallback(
    (type: SelectorType, selector: string) => {
      if (!internalAdmin) return;
      if (isSelectorFromOriginal(selector)) return;
      const key = normalizeSelector(selector);
      if (!key) return;

      setJsConfig((prev: any) => {
        const next: any = { ...(prev || EMPTY_JS_CONFIG) };
        const listKey =
          type === 'block' ? 'blockSelector' : type === 'ignore' ? 'ignoreSelector' : 'maskTextSelector';
        const current: string[] = [...(next[listKey] || [])];
        next[listKey] = current.filter((s) => normalizeSelector(s) !== key);
        return next;
      });
    },
    [internalAdmin, isSelectorFromOriginal],
  );

  // (Replaced by toggleOtherMaskingType / toggleOtherMaskingAll)

  // Keep sessionReplayData in sync with API data when it changes
  useEffect(() => {
    const normalized = normalizeApiData(initialSessionReplayData as any);
    setApiData(normalized);
    setOriginalApiData(normalized);

    const nextJs = normalizeJsConfig((normalized as any)?.js?.config);
    setJsConfig(nextJs);
    setOriginalJsConfig(nextJs);

    const nextAndroid = normalizeAndroidConfig((normalized as any)?.android?.config);
    setAndroidConfig(nextAndroid);
    setOriginalAndroidConfig(nextAndroid);

    const nextIos = normalizeIosConfig((normalized as any)?.ios?.config);
    setIosConfig(nextIos);
    setOriginalIosConfig(nextIos);
    setDeletedSelectors(new Set());
  }, [initialSessionReplayData]);

  // Mask type is now the single source of truth for "Other Masking Types" UI.

  const isDirty = useMemo(() => {
    const jsChanged =
      Boolean(jsConfig?.enabled) !== Boolean(originalJsConfig?.enabled) ||
      jsConfig?.samplingPct !== originalJsConfig?.samplingPct ||
      Boolean(jsConfig?.maskAllInputs) !== Boolean(originalJsConfig?.maskAllInputs) ||
      !arraysEqual(
        jsConfig?.maskAllInputs ? OTHER_MASKING_TYPES : normalizeMaskInputOptions(jsConfig?.maskInputOptions),
        originalJsConfig?.maskAllInputs
          ? OTHER_MASKING_TYPES
          : normalizeMaskInputOptions(originalJsConfig?.maskInputOptions),
      ) ||
      Boolean(jsConfig?.mousemove) !== Boolean(originalJsConfig?.mousemove) ||
      !arraysEqual(jsConfig?.blockSelector, originalJsConfig?.blockSelector) ||
      !arraysEqual(jsConfig?.ignoreSelector, originalJsConfig?.ignoreSelector) ||
      !arraysEqual(jsConfig?.maskTextSelector, originalJsConfig?.maskTextSelector);

    const androidChanged =
      Boolean(androidConfig?.enabled) !== Boolean(originalAndroidConfig?.enabled) ||
      androidConfig?.samplingPct !== originalAndroidConfig?.samplingPct ||
      Boolean(androidConfig?.maskAllInputs) !== Boolean(originalAndroidConfig?.maskAllInputs) ||
      !arraysEqual(
        androidConfig?.maskAllInputs ? ANDROID_MASK_INPUT_OPTIONS : androidConfig?.maskInputOptions,
        originalAndroidConfig?.maskAllInputs
          ? ANDROID_MASK_INPUT_OPTIONS
          : originalAndroidConfig?.maskInputOptions,
      ) ||
      Boolean(androidConfig?.maskAllImages) !== Boolean(originalAndroidConfig?.maskAllImages) ||
      androidConfig?.compressionQuality !== originalAndroidConfig?.compressionQuality;

    const iosChanged =
      Boolean(iosConfig?.enabled) !== Boolean(originalIosConfig?.enabled) ||
      iosConfig?.samplingPct !== originalIosConfig?.samplingPct ||
      Boolean(iosConfig?.maskAllInputs) !== Boolean(originalIosConfig?.maskAllInputs) ||
      !arraysEqual(
        iosConfig?.maskAllInputs ? IOS_MASK_INPUT_OPTIONS : iosConfig?.maskInputOptions,
        originalIosConfig?.maskAllInputs ? IOS_MASK_INPUT_OPTIONS : originalIosConfig?.maskInputOptions,
      ) ||
      Boolean(iosConfig?.maskAllImages) !== Boolean(originalIosConfig?.maskAllImages) ||
      Boolean(iosConfig?.maskSandboxedSystemViews) !==
        Boolean(originalIosConfig?.maskSandboxedSystemViews) ||
      iosConfig?.compressionQuality !== originalIosConfig?.compressionQuality;

    return jsChanged || androidChanged || iosChanged || deletedSelectors.size > 0;
  }, [
    jsConfig,
    originalJsConfig,
    androidConfig,
    originalAndroidConfig,
    iosConfig,
    originalIosConfig,
    deletedSelectors,
  ]);

  // Calculate count of changes in Web Configuration
  const webConfigurationChangesCount = useMemo(() => {
    let count = 0;
    // Count enabled change
    if (Boolean(jsConfig?.enabled) !== Boolean(originalJsConfig?.enabled)) {
      count += 1;
    }
    // Count samplingPct change
    if (jsConfig?.samplingPct !== originalJsConfig?.samplingPct) {
      count += 1;
    }
    // Count maskAllInputs / maskInputOptions change
    if (
      Boolean(jsConfig?.maskAllInputs) !== Boolean(originalJsConfig?.maskAllInputs) ||
      !arraysEqual(
        jsConfig?.maskAllInputs ? OTHER_MASKING_TYPES : normalizeMaskInputOptions(jsConfig?.maskInputOptions),
        originalJsConfig?.maskAllInputs
          ? OTHER_MASKING_TYPES
          : normalizeMaskInputOptions(originalJsConfig?.maskInputOptions),
      )
    ) {
      count += 1;
    }
    // Count mousemove change
    if (
      Boolean(jsConfig?.mousemove) !== Boolean(originalJsConfig?.mousemove)
    ) {
      count += 1;
    }
    // Count newly added selectors (case-insensitive, trimmed), excluding pending deletions
    const originalAll = [
      ...(originalJsConfig?.blockSelector || []),
      ...(originalJsConfig?.ignoreSelector || []),
      ...(originalJsConfig?.maskTextSelector || []),
    ].map(normalizeSelector);
    const originalSet = new Set(originalAll);
    const deletedSet = new Set([...deletedSelectors].map(normalizeSelector));
    const currentAllEffective = [
      ...(jsConfig?.blockSelector || []),
      ...(jsConfig?.ignoreSelector || []),
      ...(jsConfig?.maskTextSelector || []),
    ]
      .filter((s: string) => !deletedSet.has(normalizeSelector(s)))
      .map(normalizeSelector);
    const added = currentAllEffective.filter((s) => s && !originalSet.has(s));
    count += new Set(added).size;
    // Count deleted selectors
    count += deletedSelectors.size;
    return count;
  }, [jsConfig, originalJsConfig, deletedSelectors]);

  // Calculate count of changes in Android Configuration
  const androidConfigurationChangesCount = useMemo(() => {
    let count = 0;
    if (Boolean(androidConfig?.enabled) !== Boolean(originalAndroidConfig?.enabled)) count += 1;
    if (androidConfig?.samplingPct !== originalAndroidConfig?.samplingPct) count += 1;
    if (
      Boolean(androidConfig?.maskAllInputs) !== Boolean(originalAndroidConfig?.maskAllInputs) ||
      !arraysEqual(
        androidConfig?.maskAllInputs ? ANDROID_MASK_INPUT_OPTIONS : androidConfig?.maskInputOptions,
        originalAndroidConfig?.maskAllInputs ? ANDROID_MASK_INPUT_OPTIONS : originalAndroidConfig?.maskInputOptions,
      )
    ) {
      count += 1;
    }
    if (Boolean(androidConfig?.maskAllImages) !== Boolean(originalAndroidConfig?.maskAllImages)) count += 1;
    if (androidConfig?.compressionQuality !== originalAndroidConfig?.compressionQuality) count += 1;
    return count;
  }, [androidConfig, originalAndroidConfig]);

  // Calculate count of changes in iOS Configuration
  const iosConfigurationChangesCount = useMemo(() => {
    let count = 0;
    if (Boolean(iosConfig?.enabled) !== Boolean(originalIosConfig?.enabled)) count += 1;
    if (iosConfig?.samplingPct !== originalIosConfig?.samplingPct) count += 1;
    if (
      Boolean(iosConfig?.maskAllInputs) !== Boolean(originalIosConfig?.maskAllInputs) ||
      !arraysEqual(
        iosConfig?.maskAllInputs ? IOS_MASK_INPUT_OPTIONS : iosConfig?.maskInputOptions,
        originalIosConfig?.maskAllInputs ? IOS_MASK_INPUT_OPTIONS : originalIosConfig?.maskInputOptions,
      )
    ) {
      count += 1;
    }
    if (Boolean(iosConfig?.maskAllImages) !== Boolean(originalIosConfig?.maskAllImages)) count += 1;
    if (
      Boolean(iosConfig?.maskSandboxedSystemViews) !== Boolean(originalIosConfig?.maskSandboxedSystemViews)
    ) {
      count += 1;
    }
    if (iosConfig?.compressionQuality !== originalIosConfig?.compressionQuality) count += 1;
    return count;
  }, [iosConfig, originalIosConfig]);

  // Helper to render tab labels with icons and change counts
  const renderTabLabel = useCallback(
    (label: string, icon: React.ReactNode, count: number) => (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 16,
          fontWeight: 400,
          padding: '8px 14px',
        }}
      >
        {icon}
        <span style={{ fontWeight: 'bold' }}>{label}</span>
        {count > 0 && (
          <span
            style={{
              backgroundColor: 'rgba(255, 231, 186, 1)',
              color: 'rgba(173, 78, 0, 1)',
              padding: '2px 6px',
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            {count}
          </span>
        )}
      </span>
    ),
    [],
  );

  const handleSaveSessionReplay = () => {
    const updatedData: any = { ...(jsConfig || EMPTY_JS_CONFIG) };
    const jsNode = (apiData as any)?.js as SessionReplayPlatformNode | undefined;
    // allow configuring even if exists === false; backend should create/update on save

    const deletedSet = new Set([...deletedSelectors].map(normalizeSelector));
    const effective = (arr: string[]) =>
      (arr || []).filter((s) => !deletedSet.has(normalizeSelector(s)));

    const block = effective(updatedData.blockSelector || []);
    const ignore = effective(updatedData.ignoreSelector || []);
    const mask = effective(updatedData.maskTextSelector || []);

    // Ensure uniqueness across all three types (case-insensitive, trimmed)
    const all = [...block, ...ignore, ...mask].map(normalizeSelector).filter(Boolean);
    if (new Set(all).size !== all.length) {
      message.error(
        'Duplicate CSS selectors found across types. Please ensure all selectors are unique (case-insensitive, trimmed).',
      );
      return;
    }

    updatedData.blockSelector = block;
    updatedData.ignoreSelector = ignore;
    updatedData.maskTextSelector = mask;
    updatedData.samplingPct = clampPercentage(updatedData.samplingPct ?? 10);
    updatedData.maskInputOptions = normalizeMaskInputOptions(updatedData.maskInputOptions);
    if (updatedData.maskAllInputs) {
      updatedData.maskInputOptions = OTHER_MASKING_TYPES;
    } else if (updatedData.maskInputOptions.length === OTHER_MASKING_TYPES.length) {
      // keep maskAllInputs consistent if user selected everything manually
      updatedData.maskAllInputs = true;
    }

    setIsSaving(true); // Start spinner
    const otherPlatformsConfigPayload: Partial<Record<'android' | 'ios', any>> = {};
    (['android', 'ios'] as const).forEach((platform) => {
      const node = (apiData as any)?.[platform] as SessionReplayPlatformNode | undefined;
      const baseConfig =
        node?.config && typeof node.config === 'object' && !Array.isArray(node.config)
          ? { ...(node.config as any) }
          : {};
      const draft = platform === 'android' ? androidConfig : iosConfig;

      const allowed = platform === 'android' ? ANDROID_MASK_INPUT_OPTIONS : IOS_MASK_INPUT_OPTIONS;
      // Defaults are implicit (shown separately in UI), not part of maskInputOptions payload.

      const samplingPct = clampPercentage(draft?.samplingPct ?? 10);
      const maskAllInputs = Boolean((draft as any)?.maskAllInputs);
      let maskInputOptions = Array.isArray((draft as any)?.maskInputOptions)
        ? ((draft as any).maskInputOptions as string[])
        : [];
      const optionSet = new Set(maskInputOptions);
      maskInputOptions = allowed.filter((t) => optionSet.has(t));
      if (maskAllInputs) {
        maskInputOptions = allowed;
      }
      const maskAllInputsFinal = maskAllInputs || maskInputOptions.length === allowed.length;
      if (maskAllInputsFinal) maskInputOptions = allowed;

      const mergedConfig: any =
        platform === 'android'
          ? {
              ...baseConfig,
              enabled: Boolean((draft as AndroidDraftConfig).enabled),
              samplingPct,
              maskAllInputs: maskAllInputsFinal,
              maskInputOptions,
              maskAllImages: Boolean((draft as AndroidDraftConfig).maskAllImages),
              compressionQuality: normalizeCompressionQuality(
                (draft as AndroidDraftConfig).compressionQuality,
              ),
            }
          : {
              ...baseConfig,
              enabled: Boolean((draft as IosDraftConfig).enabled),
              samplingPct,
              maskAllInputs: maskAllInputsFinal,
              maskInputOptions,
              maskAllImages: Boolean((draft as IosDraftConfig).maskAllImages),
              maskSandboxedSystemViews: Boolean(
                (draft as IosDraftConfig).maskSandboxedSystemViews,
              ),
              compressionQuality: normalizeCompressionQuality((draft as IosDraftConfig).compressionQuality),
            };

      otherPlatformsConfigPayload[platform] = mergedConfig;
    });

    createSessionReplay(
      {
        queryBody: {
          ...otherPlatformsConfigPayload,
          js: {
            // Preserve any unknown keys the backend might add
            ...((jsNode as any)?.config || {}),
            enabled: updatedData.enabled,
            samplingPct: updatedData.samplingPct,
            blockSelector: updatedData.blockSelector || [],
            ignoreSelector: updatedData.ignoreSelector || [],
            maskTextSelector: updatedData.maskTextSelector || [],
            mousemove: updatedData.mousemove,
            maskAllInputs: updatedData.maskAllInputs,
            maskInputOptions: updatedData.maskAllInputs ? OTHER_MASKING_TYPES : updatedData.maskInputOptions,
          },
        },
      },
      {
        onSuccess: () => {
          refetchSessionReplayData();
          const nextApi: SessionReplayApiData = {
            ...(apiData || {}),
            js: {
              exists: (jsNode as any)?.exists ?? true,
              config: {
                ...((jsNode as any)?.config || {}),
                enabled: updatedData.enabled,
                samplingPct: updatedData.samplingPct,
                blockSelector: updatedData.blockSelector || [],
                ignoreSelector: updatedData.ignoreSelector || [],
                maskTextSelector: updatedData.maskTextSelector || [],
                mousemove: updatedData.mousemove,
                maskAllInputs: updatedData.maskAllInputs,
                maskInputOptions: updatedData.maskAllInputs ? OTHER_MASKING_TYPES : updatedData.maskInputOptions,
              },
            },
          };
          if (otherPlatformsConfigPayload.android) {
            nextApi.android = { exists: true, config: otherPlatformsConfigPayload.android };
          }
          if (otherPlatformsConfigPayload.ios) {
            nextApi.ios = { exists: true, config: otherPlatformsConfigPayload.ios };
          }
          setApiData(nextApi);
          setOriginalApiData(nextApi);
          setJsConfig(normalizeJsConfig((nextApi as any)?.js?.config));
          setOriginalJsConfig(normalizeJsConfig((nextApi as any)?.js?.config));
          setAndroidConfig(normalizeAndroidConfig((nextApi as any)?.android?.config));
          setOriginalAndroidConfig(normalizeAndroidConfig((nextApi as any)?.android?.config));
          setIosConfig(normalizeIosConfig((nextApi as any)?.ios?.config));
          setOriginalIosConfig(normalizeIosConfig((nextApi as any)?.ios?.config));
          setDeletedSelectors(new Set());
          setIsSaving(false); // Stop spinner
          message.success({
            content: (
              <div style={{ textAlign: 'left' }}>
                Cohort Replay settings saved successfully.
                <br />
                Sensors may take up to 5 minutes to reflect changes.
              </div>
            ),
            duration: 5,
          });
        },
        onError: (error: any) => {
          setIsSaving(false); // Stop spinner
          if (error?.response?.data?.msg) {
            message.error(error?.response?.data?.msg || 'Failed to save Cohort Replay settings');
          }
        },
      },
    );
  };

  const handleDiscardAll = () => {
    setApiData(originalApiData);
    setJsConfig(normalizeJsConfig((originalApiData as any)?.js?.config));
    setAndroidConfig(normalizeAndroidConfig((originalApiData as any)?.android?.config));
    setIosConfig(normalizeIosConfig((originalApiData as any)?.ios?.config));
    setDeletedSelectors(new Set());
  };

  const handleDeleteSelector = useCallback((selector: string) => {
    setDeletedSelectors((prev) => new Set(prev).add(selector));
  }, []);

  const handleRestoreSelector = useCallback((selector: string) => {
    setDeletedSelectors((prev) => {
      const newSet = new Set(prev);
      newSet.delete(selector);
      return newSet;
    });
  }, []);

  return (
    <Spin spinning={isSaving || isLoadingSessionReplayData || isFetchingSessionReplayData}>
      <div
        style={{
          padding: '8px 16px',
          maxHeight: 'calc(100vh - 150px)',
          overflowY: 'auto',
          paddingRight: '8px',
          paddingBottom: '70px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ fontSize: '20px', fontWeight: '500' }} data-test-id="cohortReplayHeader">
            Cohort Replay
          </span>
        </div>
        <div style={{ marginTop: 8 }}>
          <Text
            style={{
              fontSize: '14px',
              color: '#8c8c8c',
              fontWeight: 400,
            }}
            data-test-id="sessionReplayDescription"
          >
            Cohort Replay enables a visual reconstruction and review of user interactions to analyze
            user experience. These Replays capture clicks, navigation, scrolling, media engagements,
            and text input, and present them in a streamlined, video-like playback experience for web and snapshot sequences for mobile sessions.
          </Text>
          <div>
            {!showFullDescription ? (
              <div style={{ marginTop: 8 }}>
                <span
                  onClick={() => setShowFullDescription(true)}
                  style={{
                    color: 'rgba(0, 70, 255, 1)',
                    cursor: 'pointer',
                    textDecoration: 'none',
                  }}
                  data-test-id="sessionReplayViewMore"
                >
                  View More
                </span>
              </div>
            ) : (
              <>
                <Text style={{ display: 'block', marginTop: '8px' }}>Benefits: </Text>
                <Text
                  style={{
                    fontSize: '14px',
                    color: '#8c8c8c',
                    fontWeight: 400,
                  }}
                >
                  <ul style={{ listStyle: 'none', margin: 0 }}>
                    <li style={{ position: 'relative', paddingLeft: '14px' }}>
                      <BulletCustom />
                      Understand the details in user behaviors and friction points
                    </li>
                    <li style={{ position: 'relative', paddingLeft: '14px' }}>
                      <BulletCustom />
                      Link from Funnels and Metric anomalies to visual behavior representations for
                      quick user behavior optimizations.
                    </li>
                    <li style={{ position: 'relative', paddingLeft: '14px' }}>
                      <BulletCustom />
                      Monitor the exact sequences of user actions for behavior analysis and UI
                      improvements.
                    </li>
                  </ul>
                </Text>
                <div style={{ display: 'flex', marginTop: '8px' }}>
                  <Text>Supported Platforms and Versions:</Text>
                  <Text
                    style={{
                      fontSize: '14px',
                      color: '#8c8c8c',
                      fontWeight: 400,
                      paddingLeft: '4px',
                    }}
                  >
                    {' '}
                    Web (JS) version 1.4.3 and above. For Android and iOS, please reach out to Conviva representative. <div style={{ marginTop: 8 }} />
                  </Text>
                </div>
                <div style={{ marginTop: 8 }}>
                  <span
                    onClick={() => setShowFullDescription(false)}
                    style={{
                      color: 'rgba(0, 70, 255, 1)',
                      cursor: 'pointer',
                      textDecoration: 'none',
                    }}
                    data-test-id="sessionReplayViewLess"
                  >
                    View Less
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
        <>
          <Divider style={{ marginTop: '8px' ,width: '300px' }} />
          <div >
            <Tabs
              size="small"
              tabBarGutter={24}
              tabBarStyle={{
                marginBottom: 20,
                position: 'sticky',
                top: 0,
                zIndex: 10,
                height: 60,
                backgroundColor: '#fff',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                borderRadius: 16,
              }}
              defaultActiveKey="js"
              data-test-id="platformConfigurationTabs"
              items={[
                {
                  key: 'js',
                  label: renderTabLabel('Web', <GlobalOutlined />, webConfigurationChangesCount),
                  children: (
                    <div style={{ marginTop: 8, marginBottom: 16, marginLeft: 8, marginRight: 8 }}>
                      <EnableCohortReplayCard
                        platform="Web (JS)"
                        testId="enableCohortReplaySwitch"
                        checked={Boolean(jsConfig?.enabled)}
                        disabled={!internalAdmin}
                        onChange={(checked) => {
                          setJsConfig((prev) => ({
                            ...(prev || EMPTY_JS_CONFIG),
                            enabled: checked,
                          }));
                        }}
                      />
                      <div style={{ height: 16 }} />
                      {jsConfig?.enabled ? (
                        <>
                          <TrafficSamplingSection
                            value={jsConfig?.samplingPct ?? 0}
                            canEdit={canEdit}
                            inputTestId="samplingPercentageInput"
                            sliderTestId="samplingPercentageSlider"
                            readOnlyTestId="samplingPercentageReadOnly"
                            onChange={(nextVal) => {
                              setJsConfig((prev) => ({ ...(prev || EMPTY_JS_CONFIG), samplingPct: nextVal }));
                            }}
                          />

                        <div style={{ height: 24 }} />

                        <section style={cardStyle}>
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 16,
                              marginBottom: 12,
                            }}
                          >
                            <div>
                              <div style={{ fontSize: 18}}>
                                Masking of User Information
                              </div>
                              <div style={{ fontSize: 14, color: 'rgba(100, 116, 139, 1)' }}>
                              Mask PII and sensitive information in Cohort Replays across the applications.
                              </div>
                            </div>
                            
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            <div
                              style={{
                                background: 'rgba(248, 250, 252, 1)',
                                border: '1px solid rgba(226, 232, 240, 1)',
                                borderRadius: 16,
                                padding: 16,
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                
                                <span style={{ fontSize: 10, fontWeight: 800, color: 'rgba(71, 85, 105, 1)', background: 'rgba(226, 232, 240, 1)', padding: '2px 8px', borderRadius: 8 }}>
                                  DEFAULT
                                </span>
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {LOCKED_MANUAL_BADGES.map((item) => (
                                  <span
                                    key={item}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 8,
                                      padding: '6px 10px',
                                      borderRadius: 12,
                                      background: '#fff',
                                      border: '1px solid rgba(226, 232, 240, 1)',
                                      color: 'rgba(51, 65, 85, 1)',
                                      fontSize: 12,
                                      fontWeight: 700,
                                    }}
                                  >
                                    <CheckOutlined style={{ color: 'rgba(148, 163, 184, 1)' }} />
                                    {item}
                                  </span>
                                ))}
                              </div>
                            </div>

                            <div>
                              <div style={{ fontSize: 14, marginBottom: 10 }}>
                               Additional Masking Types
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 10 }}>
                              <button
                                  data-test-id="maskInputOptionsAllWeb"
                                  type="button"
                                  onClick={toggleOtherMaskingAll}
                                  disabled={!internalAdmin}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    padding: '10px 12px',
                                    borderRadius: 12,
                                    border: (allOtherMaskingTypesSelected || someOtherMaskingTypesSelected)
                                      ? '1px solid rgba(79, 70, 229, 1)'
                                      : '1px solid rgba(226, 232, 240, 1)',
                                    background: '#fff',
                                    cursor: internalAdmin ? 'pointer' : 'not-allowed',
                                    opacity: internalAdmin ? 1 : 0.5,
                                  }}
                                >
                                  <Checkbox
                                    checked={allOtherMaskingTypesSelected}
                                    indeterminate={someOtherMaskingTypesSelected}
                                    disabled={!internalAdmin}
                                  />
                                  <span style={{ fontSize: 14, color: 'rgba(30, 41, 59, 1)' }}>All</span>
                                </button>
                              <button
                                  data-test-id={`maskInputOptionWeb-${SEARCH_EXTENSION}`}
                                  type="button"
                                  onClick={() => toggleOtherMaskingType(SEARCH_EXTENSION)}
                                  disabled={!internalAdmin}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    padding: '10px 12px',
                                    borderRadius: 12,
                                    border: manualSearchSelected
                                      ? '1px solid rgba(79, 70, 229, 1)'
                                      : '1px solid rgba(226, 232, 240, 1)',
                                    background: '#fff',
                                    cursor: internalAdmin ? 'pointer' : 'not-allowed',
                                    opacity: internalAdmin ? 1 : 0.5,
                                  }}
                                >
                                  
                                  <Checkbox checked={manualSearchSelected} disabled={!internalAdmin} />
                                  <span
                                    style={{
                                      fontSize: 14,
                                      
                                      //color: manualSearchSelected ? 'rgba(30, 41, 59, 1)' : 'rgba(148, 163, 184, 1)',
                                    }}
                                  >
                                    {SEARCH_EXTENSION}
                                  </span>
                                  
                                </button>
                              
                                {ADVANCED_BULK_EXTENSIONS.map((opt) => {
                                  const checked = selectedOtherMaskingTypes.includes(opt);
                                  return (
                                    <button
                                      key={opt}
                                      data-test-id={`maskInputOptionWeb-${opt}`}
                                      type="button"
                                      onClick={() => toggleOtherMaskingType(opt)}
                                      disabled={!internalAdmin}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        padding: '10px 12px',
                                        borderRadius: 12,
                                        border: checked
                                          ? '1px solid rgba(79, 70, 229, 1)'
                                          : '1px solid rgba(226, 232, 240, 1)',
                                        background: '#fff',
                                        cursor: internalAdmin ? 'pointer' : 'not-allowed',
                                        opacity: internalAdmin ? 1 : 0.5,
                                      }}
                                    >
                                      <Checkbox checked={checked} disabled={!internalAdmin} />
                                      <span style={{ fontSize: 14 }}>{opt}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </section>

                        <div style={{ height: 24 }} />

                        <section style={{ ...cardStyle, paddingTop: 20 }}>
                          <label style={{ fontSize: 18, display: 'block', marginBottom: 12 }}>
                          Custom Masking (CSS)
                          </label>
                          <div style={{ color: 'rgba(100, 116, 139, 1)', fontSize: 14, marginBottom: 10 }}>
                          Block, ignore, or mask elements using CSS selectors along with the above selected masking elements.
                          </div>
                        
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
                            <TagInput
                              label="Block Selector"
                              description=" Prevents element tracking so they do not appear in the replay."
                              tags={jsConfig?.blockSelector || []}
                              isDeleted={isSelectorDeleted}
                              isFromOriginal={isSelectorFromOriginal}
                              onAdd={(v) => addSelectorToType('block', v)}
                              onRemoveNew={(v) => removeNewSelectorFromType('block', v)}
                              onDeleteExisting={handleDeleteSelector}
                              onRestoreExisting={handleRestoreSelector}
                              placeholder=".block-this, #secret-div"
                              disabled={!internalAdmin}
                            />
                            <TagInput
                              label="Ignore Selector"
                              description=" Skips tracking interactions on selected elements while keeping them visible in the replay."
                              tags={jsConfig?.ignoreSelector || []}
                              isDeleted={isSelectorDeleted}
                              isFromOriginal={isSelectorFromOriginal}
                              onAdd={(v) => addSelectorToType('ignore', v)}
                              onRemoveNew={(v) => removeNewSelectorFromType('ignore', v)}
                              onDeleteExisting={handleDeleteSelector}
                              onRestoreExisting={handleRestoreSelector}
                              placeholder=".ignore-me"
                              disabled={!internalAdmin}
                            />
                            <TagInput
                              label="Mask Text Selector"
                              description=" Tracks element interactions and displays elements in the replay while masking sensitive information."
                              tags={jsConfig?.maskTextSelector || []}
                              isDeleted={isSelectorDeleted}
                              isFromOriginal={isSelectorFromOriginal}
                              onAdd={(v) => addSelectorToType('mask', v)}
                              onRemoveNew={(v) => removeNewSelectorFromType('mask', v)}
                              onDeleteExisting={handleDeleteSelector}
                              onRestoreExisting={handleRestoreSelector}
                              placeholder=".mask-content"
                              disabled={!internalAdmin}
                            />
                          </div>
                        </section>

                        <div style={{ height: 24 }} />
                          <ToggleRow
                              title="Mouse Trail"
                                  description="Tracks/replays mouse movements for end users."
                                  checked={jsConfig?.mousemove}
                                  onChange={(checked) =>
                                    setJsConfig(prev => ({
                                      ...prev,
                                      mousemove: checked
                                    }))
                                  }
                                  disabled={!internalAdmin}
                                  testId="mouseTrailSwitch"
                                />
                                </>
                      ) : null}
                    </div>
                  ),
                },
                {
                  key: 'android',
                  label: renderTabLabel('Android', <AndroidOutlined />, androidConfigurationChangesCount),
                  children: (
                    <div style={{ marginTop: 8, marginBottom: 16, marginLeft: 8, marginRight: 8 }}>
                      <EnableCohortReplayCard
                        platform="Android"
                        testId="enableCohortReplaySwitchAndroid"
                        checked={Boolean(androidConfig?.enabled)}
                        disabled={!internalAdmin}
                        onChange={(checked) => {
                          setAndroidConfig((prev) => ({ ...(prev || EMPTY_ANDROID_CONFIG), enabled: checked }));
                        }}
                      />
                      <div style={{ height: 16 }} />
                      <>      
                        {androidConfig?.enabled ? (
                          <>
                          <TrafficSamplingSection
                            value={androidConfig?.samplingPct ?? 0}
                            canEdit={canEdit}
                            inputTestId="samplingPercentageInputAndroid"
                            sliderTestId="samplingPercentageSliderAndroid"
                            readOnlyTestId="samplingPercentageReadOnlyAndroid"
                            onChange={(nextVal) => {
                              setAndroidConfig((prev) => ({ ...(prev || EMPTY_ANDROID_CONFIG), samplingPct: nextVal }));
                            }}
                          />

                          <div style={{ height: 24 }} />

                          <MobileMaskingSection
                            defaultOptions={ANDROID_DEFAULT_MASK_INPUT_OPTIONS}
                            allowedOptions={ANDROID_MASK_INPUT_OPTIONS}
                            config={androidConfig}
                            canEdit={canEdit}
                            onToggleAll={toggleAndroidMaskingAll}
                            onToggleType={toggleAndroidMaskingType}
                            testIdAll="maskInputOptionsAllAndroid"
                            testIdForOption={(opt) => `maskInputOptionAndroid-${opt}`}
                            isChecked={(opt) =>
                              Boolean(androidConfig?.maskAllInputs) || Boolean(androidConfig?.maskInputOptions?.includes(opt))
                            }
                          />

                          <div style={{ height: 24 }} />

                          <MobileAdvancedFeaturesSection
                            canEdit={canEdit}
                            maskAllImages={Boolean(androidConfig?.maskAllImages)}
                            onMaskAllImagesChange={(checked) => {
                              setAndroidConfig((prev) => ({ ...(prev || EMPTY_ANDROID_CONFIG), maskAllImages: checked }));
                            }}
                            maskAllImagesTestId="maskAllImagesSwitchAndroid"
                            compressionQuality={androidConfig?.compressionQuality ?? 'low'}
                            onCompressionQualityChange={(q) => {
                              setAndroidConfig((prev) => ({ ...(prev || EMPTY_ANDROID_CONFIG), compressionQuality: q }));
                            }}
                            compressionRadioTestId="compressionQualityRadioAndroid"
                            compressionOptionTestId={(q) => `compressionQualityOptionAndroid-${q}`}
                          />
                        </>
                        ): null}
                      </>
                    </div>
                  ),
                },
                {
                  key: 'ios',
                  label: renderTabLabel('iOS', <AppleOutlined />, iosConfigurationChangesCount),
                  children: (
                    <div style={{ marginTop: 8, marginBottom: 16, marginLeft: 8, marginRight: 8 }}>
                      <EnableCohortReplayCard
                        platform="iOS"
                        testId="enableCohortReplaySwitchIos"
                        checked={Boolean(iosConfig?.enabled)}
                        disabled={!internalAdmin}
                        onChange={(checked) => {
                          setIosConfig((prev) => ({ ...(prev || EMPTY_IOS_CONFIG), enabled: checked }));
                        }}
                      />

                      <div style={{ height: 16 }} />

                      <>
                        
                        {iosConfig?.enabled ? (
                          <>
                          <TrafficSamplingSection
                            value={iosConfig?.samplingPct ?? 0}
                            canEdit={canEdit}
                            inputTestId="samplingPercentageInputIos"
                            sliderTestId="samplingPercentageSliderIos"
                            readOnlyTestId="samplingPercentageReadOnlyIos"
                            onChange={(nextVal) => {
                              setIosConfig((prev) => ({ ...(prev || EMPTY_IOS_CONFIG), samplingPct: nextVal }));
                            }}
                          />

                          <div style={{ height: 24 }} />

                          <MobileMaskingSection
                            defaultOptions={IOS_DEFAULT_MASK_INPUT_OPTIONS}
                            allowedOptions={IOS_MASK_INPUT_OPTIONS}
                            config={iosConfig}
                            canEdit={canEdit}
                            onToggleAll={toggleIosMaskingAll}
                            onToggleType={toggleIosMaskingType}
                            testIdAll="maskInputOptionsAllIos"
                            testIdForOption={(opt) => `maskInputOptionIos-${opt}`}
                            isChecked={(opt) =>
                              Boolean(iosConfig?.maskAllInputs) || Boolean(iosConfig?.maskInputOptions?.includes(opt))
                            }
                            gridStyle={{ overflowY: 'auto', paddingRight: 8 }}
                          />

                          <div style={{ height: 24 }} />

                          <MobileAdvancedFeaturesSection
                            canEdit={canEdit}
                            maskAllImages={Boolean(iosConfig?.maskAllImages)}
                            onMaskAllImagesChange={(checked) => {
                              setIosConfig((prev) => ({ ...(prev || EMPTY_IOS_CONFIG), maskAllImages: checked }));
                            }}
                            maskAllImagesTestId="maskAllImagesSwitchIos"
                            compressionQuality={iosConfig?.compressionQuality ?? 'low'}
                            onCompressionQualityChange={(q) => {
                              setIosConfig((prev) => ({ ...(prev || EMPTY_IOS_CONFIG), compressionQuality: q }));
                            }}
                            compressionRadioTestId="compressionQualityRadioIos"
                            compressionOptionTestId={(q) => `compressionQualityOptionIos-${q}`}
                            // extraToggles={[
                            //   {
                            //     title: 'Mask Sandboxed System Views',
                            //     description: 'Mask content from sandboxed system views.',
                            //     checked: Boolean(iosConfig?.maskSandboxedSystemViews),
                            //     testId: 'maskSandboxedSystemViewsSwitchIos',
                            //     onChange: (checked) => {
                            //       setIosConfig((prev) => ({
                            //         ...(prev || EMPTY_IOS_CONFIG),
                            //         maskSandboxedSystemViews: checked,
                            //       }));
                            //     },
                            //   },
                            // ]}
                          />
                        </>
                        ) :  null}
                      </>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </>
        
        {isDirty && (
            <div
            style={{
              position: 'fixed',
              left: '50%',
              bottom: 'calc(24px + env(safe-area-inset-bottom))',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              background: '#fff',
              padding: '14px 10px',
              borderRadius: 16,
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              color: 'greyText',
              width: '70%',
              marginLeft: '5%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
            aria-live="polite"
            >
              <span
                style={{
                  color: 'rgba(31, 31, 31, 1)',
                  fontWeight: 400,
                  fontSize: 14,
                  marginLeft: 32,
                  flex: 1,
                  width: 'auto',
                  
                }}
                data-test-id="unsavedChangesWarning"
              >
                Click save to apply changes made across all 3 platforms
              </span>
              <Button
                type="default"
                icon={<DeleteOutlined />}
                onClick={handleDiscardAll}
                style={{
                  color: 'rgba(255, 77, 79, 1)',
                  borderColor: 'rgba(255, 77, 79, 1)',
                  marginLeft: 10,
                }}
                data-test-id="discard-all-button"
              >
                Discard All
              </Button>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                data-test-id="saveSessionReplayButton"
                onClick={handleSaveSessionReplay}
                style={{
                  marginRight: 32,
                  marginLeft: 10,
                }}
              >
                Save
              </Button>
            </div>
        )}
      </div>
    </Spin>
  );
};

export default SessionReplay;
